import {
  isPartyMemberCapError,
  PARTY_MEMBER_CAP_ERROR_CODE,
  partyMemberCapMessage,
} from "./party-entitlements";
import type { PartyJoinAttribution } from "./party-join-attribution";
import type { CreatePartyResponse } from "./types/parties-api";

export type JoinPartyFullResult = {
  status: "full";
  code: typeof PARTY_MEMBER_CAP_ERROR_CODE;
  message: string;
};

export type JoinPartyJoinedResult = {
  status: "joined";
  party: CreatePartyResponse;
  attribution?: PartyJoinAttribution;
};

export type JoinPartyResult = JoinPartyJoinedResult | JoinPartyFullResult;

export function isJoinPartyFullResult(
  result: JoinPartyResult,
): result is JoinPartyFullResult {
  return result.status === "full";
}

export function joinPartyFullResult(message: string): JoinPartyFullResult {
  return {
    status: "full",
    code: PARTY_MEMBER_CAP_ERROR_CODE,
    message,
  };
}

export function joinPartyJoinedResult(
  party: CreatePartyResponse,
  attribution?: PartyJoinAttribution | null,
): JoinPartyJoinedResult {
  if (!attribution) {
    return { status: "joined", party };
  }
  return { status: "joined", party, attribution };
}

export function joinPartyResultFromUnknownError(
  error: unknown,
): JoinPartyFullResult | null {
  if (!isPartyMemberCapError(error)) {
    return null;
  }

  const message =
    error instanceof Error && error.message.trim()
      ? error.message
      : partyMemberCapMessage("guest");

  return joinPartyFullResult(message);
}

/**
 * Maps a join attempt to a handled result.
 * A full free party is an expected outcome, not an uncaught throw.
 */
export async function settleJoinPartyResult(
  run: () => Promise<JoinPartyResult>,
): Promise<JoinPartyResult> {
  try {
    return await run();
  } catch (error) {
    const handled = joinPartyResultFromUnknownError(error);
    if (handled) {
      return handled;
    }
    throw error;
  }
}
